import { DataLogStatus } from 'store/app/enums';
import type { User } from 'store/auth/models';
import { DataJournal, VatReportData } from 'store/vat/models/vatReportData';
import { VatReturnLog } from 'store/vat/models/vatReturnLog';

export type { DataJournal } from 'store/vat/models/vatReportData';
export type {
  AccountFilter,
  TaxRateFilter,
} from 'store/vat/models/vatReportData';

export interface ReconciliationGenerateParams {
  connectionId: string;
  mappingId: string;
  toDate: string;
  currency?: string;
  pageSize?: number;
  reportId: string;
}

export interface ReconciliationViewParams {
  organisationId: string;
  dataLogId: string;
  connectionId: string;
  mappingId: string;
  currency: string;
}

export interface ReconciliationQueryParams {
  reportId: string;
  page: number;
  pageSize: number;
  transactionType: TransactionTypeFilter;
  groupingType: string;
  filters?: QueryFilters;
}

export interface QueryFilters {
  accounts?: string[];
  taxRates?: string[];
  [key: string]: string[] | undefined;
}

export enum TransactionTypeFilter {
  TRANSACTION = 'TRANSACTION',
  EXCEPTION = 'EXCEPTION',
  OTHER = 'OTHER',
}

export enum ReconciliationDataStatus {
  IN_PROGRESS = 'IN_PROGRESS',
  RETRY_AFTER = 'RETRY_AFTER',
}

/** Response body from the generate reconciliation endpoint (`/reconciliation/generate/...`). */
export interface GenerateReconciliationDataResponse extends VatReportData {}

/** Response body from the view saved reconciliation report endpoint (`/view/reconciliation/...`). */
export interface ViewReconciliationDataResponse {
  code: string | null;
  data: any;
  dataLog: VatReturnLog;
  message: string;
  reconTotal: ReconTotal;
  success: boolean;
}

export interface ReconciliationQueryResponse {
  success: boolean;
  message: string;
  journals: DataJournal[];
  transactionsSize: number;
  exceptionsSize: number;
}

export interface ReconciliationHeaderData {
  salesTax: string;
  accountCode: string;
  accountName: string;
  accountId: string;
}

export interface ReconTotal {
  openDataLogId: string;
  openTransactionsCount: number;
  openExceptionsCount: number;
  totalTaxOpenTransactions: number;
  totalTaxOpenExceptions: number;
  gtBalance: number;
  xeroBalance: number;
  difference: number;
  totalTaxBalance: number;
  account: string;
  /** Open VAT return log status (open-period section), or null if none. */
  savedStatus: DataLogStatus | null;
}

export interface ReconDataLog {
  uuid: string;
  /** VAT return mapping this published recon belongs to (logs list / view). */
  mappingId?: string;
  /** Reporting period end (ISO date string from API). */
  toDate: string;
  savedByUserId: string;
  /** When the reconciliation was generated (ISO date-time string from API). */
  generatedDate: string;
  gtBalance: number | null;
  xeroBalance: number | null;
  difference: number | null;
  preparerUser: User | null;
}

export interface JsonStringsRequest {
  reconTotal: ReconTotal;
  totalTaxTransactions: number;
  totalTaxExceptions: number;
  totalTaxOthers: number;
  transactionsCount: number;
  exceptionsCount: number;
  othersCount: number;
}

export interface SaveReconciliationReportParams {
  organisationId: string;
  mappingId: string;
  fromDate: string;
  toDate: string;
  generatedDate: string;
  currency: string;
  reportingTypeId: string;
  reportId: string;
  jsonStringsRequest: JsonStringsRequest;
}
